import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, Clock, FolderTree, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Segmented } from '@/components/Segmented';
import { Sheet } from '@/components/Sheet';
import { AssignToBlockSheet } from '@/features/calendars/AssignToBlockSheet';
import { ConflictChoiceSheet } from '@/features/calendars/ConflictChoiceSheet';
import { ScheduleSheet } from '@/features/calendars/ScheduleSheet';
import { findFreeSlot } from '@/features/calendars/estimateChoice';
import { buildOccupied } from '@/features/calendars/scheduleConflict';
import type { BusyInterval, TimeBlock } from '@/features/calendars/types';
import { blockKeys } from '@/features/calendars/useTimeBlocks';
import { useCapturePlacement } from '@/features/calendars/useCapturePlacement';
import { useCurrentUser } from '@/features/auth/useAuth';
import { useWorkBlockControls, type WorkBlock } from '@/features/time/useWorkBlocks';
import { api, type ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { fromLocalInput, toLocalInput } from '@/lib/datetimeInput';
import { SNAP_MINUTES } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { formatClock } from '@/features/time/useTimer';
import { useAutosave } from './useAutosave';
import { useCompletionNote } from './CompletionNote';
import { formatDurationClock, parseDurationSeconds } from './duration';
import { formatDuration } from './tree';
import { MoveTaskSheet } from './MoveTaskSheet';
import { RecurrenceSection, type Recurrence } from './RecurrenceSection';
import { SubtaskList } from './SubtaskList';
import {
  taskKeys,
  useClients,
  useDeleteTask,
  useProjects,
  useTask,
  useUpdateTask,
} from './useTasks';
import type { Energy, Priority, Task, TaskStatus } from './types';

interface TimeEntry {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  source: string;
  note: string | null;
}

/** The server takes one unit or the other: minutes to log, seconds to restore. */
interface LogTimeInput {
  minutes?: number;
  seconds?: number;
  occurred_at?: string;
  note?: string | null;
}

// Common estimates, one tap each. Typing a number is the fallback, not the path.
const QUICK_ESTIMATES = [5, 15, 30, 60, 120] as const;

const PRIORITIES: { value: Priority; label: string; tone?: 'danger' }[] = [
  { value: 'urgent', label: 'Urgent', tone: 'danger' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const ENERGIES: { value: Energy; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// No 'inbox': that status belongs to the review queue, which creates tasks
// already triaged into the backlog. Nothing can put a task back there.
const STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'Doing' },
  { value: 'done', label: 'Done' },
];

type ScheduleMode = 'menu' | 'time';

async function occupiedInRange(
  qc: ReturnType<typeof useQueryClient>,
  start: Date,
  end: Date,
) {
  const from = start.toISOString();
  const to = end.toISOString();
  const [blocks, busy] = await Promise.all([
    qc.fetchQuery({
      queryKey: blockKeys.range(from, to),
      queryFn: () =>
        api<TimeBlock[]>(
          `/api/time-blocks?ends_after=${encodeURIComponent(from)}` +
            `&starts_before=${encodeURIComponent(to)}`,
        ),
    }),
    qc.fetchQuery({
      queryKey: ['calendars', 'busy', from, to],
      queryFn: () =>
        api<BusyInterval[]>(
          `/api/calendars/busy?start=${encodeURIComponent(from)}` +
            `&end=${encodeURIComponent(to)}`,
        ),
    }),
  ]);
  return buildOccupied(busy, blocks);
}

/** `2026-08-25T19:00:00Z` -> `2026-08-25T15:00` for datetime-local. */
export function TaskDetail({
  task,
  onClose,
  onOpenTask,
}: {
  task: Task;
  onClose: () => void;
  /** Swaps the sheet to another task, following a subtask, or its parent. */
  onOpenTask?: (taskId: number) => void;
}) {
  const qc = useQueryClient();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const clients = useClients();
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const wbControls = useWorkBlockControls();
  const askForNote = useCompletionNote();
  const placement = useCapturePlacement({
    onPlaced: (title) => {
      toast.show(`Scheduled "${title}"`);
      setScheduleMode(null);
    },
  });

  // Local mirror so typing stays instant; the server is caught up on a debounce.
  const [draft, setDraft] = useState(task);
  const projects = useProjects(draft.client_id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode | null>(null);
  const [assigningBlock, setAssigningBlock] = useState(false);
  const [moving, setMoving] = useState(false);

  // Rollups are inclusive of the task itself; the subtask tallies are not.
  const descendantCount = Math.max(0, task.rollup_total_count - 1);
  const doneDescendants = Math.max(
    0,
    task.rollup_done_count - (task.status === 'done' ? 1 : 0),
  );
  const isDone = draft.status === 'done';

  useEffect(() => {
    setDraft(task);
    setConfirmingDelete(false);
    setScheduleMode(null);
    setMoving(false);
  }, [task.id]);

  // Keep status in sync when changed from elsewhere (agenda, Plan menu)
  // without clobbering an in-progress title edit.
  useEffect(() => {
    setDraft((d) =>
      d.status === task.status && d.completed_at === task.completed_at
        ? d
        : { ...d, status: task.status, completed_at: task.completed_at },
    );
  }, [task.status, task.completed_at]);

  // Recurrence upsert/advance updates due + Start after on the server.
  useEffect(() => {
    setDraft((d) =>
      d.due_at === task.due_at && d.defer_until === task.defer_until
        ? d
        : { ...d, due_at: task.due_at, defer_until: task.defer_until },
    );
  }, [task.due_at, task.defer_until]);

  const recurrence = useQuery({
    queryKey: ['task-recurrence', task.id],
    queryFn: () => api<Recurrence | null>(`/api/tasks/${task.id}/recurrence`),
  });
  const isRecurring = Boolean(recurrence.data);

  // Fetched by id rather than read from the caller's list: Plan and Daily both
  // open this sheet with lists that need not contain the parent.
  const parent = useTask(task.parent_task_id);

  const skipOccurrence = useMutation({
    mutationFn: () =>
      api(`/api/tasks/${task.id}/recurrence/skip-occurrence`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: ['task-recurrence', task.id] });
      toast.show('Skipped this occurrence');
      setConfirmingDelete(false);
      onClose();
    },
    onError: (error: ApiError) => toast.error(error.message),
  });

  const endSeries = useMutation({
    mutationFn: () =>
      api(`/api/tasks/${task.id}/recurrence/end-series`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: ['task-recurrence', task.id] });
      toast.show(`Ended "${draft.title}" and future repeats`);
      setConfirmingDelete(false);
      onClose();
    },
    onError: (error: ApiError) => toast.error(error.message),
  });

  const { status, push, flush } = useAutosave<Task>(
    (patch) => update.mutateAsync({ id: task.id, ...patch }),
  );

  const set = <K extends keyof Task>(key: K, value: Task[K], immediate = false) => {
    setDraft((d) => ({ ...d, [key]: value }));
    push({ [key]: value } as Partial<Task>, immediate);
  };

  const close = () => {
    flush();
    onClose();
  };

  const entries = useQuery<TimeEntry[]>({
    queryKey: ['time-entries', task.id],
    queryFn: () => api<TimeEntry[]>(`/api/time-entries?task_id=${task.id}`),
  });

  const refreshTime = () => {
    qc.invalidateQueries({ queryKey: ['time-entries', task.id] });
    qc.invalidateQueries({ queryKey: taskKeys.all });
  };

  const logTime = useMutation<unknown, ApiError, LogTimeInput>({
    mutationFn: (body) =>
      api('/api/time-entries', {
        method: 'POST',
        json: { task_id: task.id, ...body },
      }),
    onSuccess: refreshTime,
    onError: (error) => toast.error(error.message),
  });

  const updateEntry = useMutation<unknown, ApiError, { id: number; seconds: number }>({
    mutationFn: ({ id, seconds }) =>
      api(`/api/time-entries/${id}`, {
        method: 'PATCH',
        json: { seconds },
      }),
    onSuccess: refreshTime,
    onError: (error) => toast.error(error.message),
  });

  const deleteEntry = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api(`/api/time-entries/${id}`, { method: 'DELETE' }),
    onSuccess: refreshTime,
    onError: (error) => toast.error(error.message),
  });

  const removeEntry = (entry: TimeEntry) => {
    const seconds = entry.duration_seconds ?? 0;
    deleteEntry.mutate(entry.id, {
      onSuccess: () => {
        // Re-logged against the same instant it ended, so undo puts the entry
        // back on the day it was worked rather than today.
        toast.undoable(`Removed ${formatDurationClock(seconds)}`, () =>
          logTime.mutate({
            seconds,
            occurred_at: entry.ended_at ?? undefined,
            note: entry.note,
          }),
        );
      },
    });
  };

  const assignToBlock = async (block: WorkBlock) => {
    const durationMin = draft.estimate_minutes ?? SNAP_MINUTES * 2;
    const durationMs = durationMin * 60_000;
    const blockStart = new Date(block.starts_at);
    const blockEnd = new Date(block.ends_at);
    const searchMs = Math.max(0, blockEnd.getTime() - blockStart.getTime());

    setAssigningBlock(true);
    try {
      let occupied = await occupiedInRange(qc, blockStart, blockEnd);
      const slot = findFreeSlot(blockStart, durationMs, occupied, { searchMs });
      if (!slot) {
        toast.error('No open time in that block for this estimate');
        return;
      }

      const items = [
        ...block.items.map((item) => ({
          task_id: item.task_id,
          calendar_event_id: item.calendar_event_id,
          time_block_id: item.time_block_id,
          estimated_minutes: item.estimated_minutes,
          required_meeting: item.required_meeting,
        })),
        {
          task_id: task.id,
          estimated_minutes: durationMin,
          required_meeting: false,
        },
      ];

      await wbControls.updateItems.mutateAsync({ blockId: block.id, items });
      await placement.place(task.id, draft.title, slot.startsAt, slot.endsAt);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not schedule in block');
    } finally {
      setAssigningBlock(false);
    }
  };

  const clientName = clients.data?.find((c) => c.id === draft.client_id)?.name;
  const isRootTask = task.parent_task_id == null;
  const parentTitle = parent.data?.title ?? null;
  // "Top-level task" is a claim, not a placeholder: never show it while the
  // parent is still on the wire.
  const parentLabel =
    task.parent_task_id == null ? 'Top-level task' : (parentTitle ?? '…');

  return (
    <>
      <Sheet
        title={clientName ?? 'Task'}
        onClose={close}
        footer={
          confirmingDelete ? (
            <div className="flex flex-col gap-2">
              <p className="text-label text-danger">
                {isRecurring
                  ? 'This is a repeating task. Skip only this occurrence, or delete this and all future ones? Past completions stay in history.'
                  : descendantCount > 0
                    ? `Delete this and ${descendantCount} subtask${descendantCount === 1 ? '' : 's'}, with their logged time? This cannot be undone.`
                    : 'Delete this task and its logged time? This cannot be undone.'}
              </p>
              <div className="flex flex-wrap justify-end gap-1.5">
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingDelete(false)}
                  className="min-h-control px-3 text-small"
                >
                  Cancel
                </Button>
                {isRecurring ? (
                  <>
                    <Button
                      variant="secondary"
                      loading={skipOccurrence.isPending}
                      disabled={endSeries.isPending}
                      className="min-h-control px-3 text-small"
                      onClick={() => skipOccurrence.mutate()}
                    >
                      This occurrence
                    </Button>
                    <Button
                      variant="danger"
                      loading={endSeries.isPending}
                      disabled={skipOccurrence.isPending}
                      className="min-h-control px-3 text-small"
                      onClick={() => endSeries.mutate()}
                    >
                      This & future
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    className="min-h-control px-3 text-small"
                    onClick={() =>
                      remove.mutate(task.id, {
                        onSuccess: () => {
                          toast.show(`Deleted "${draft.title}"`);
                          onClose();
                        },
                      })
                    }
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <SaveIndicator status={status} />
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  aria-label="Delete task"
                  title="Delete task"
                  className={cn(
                    'pressable flex size-control items-center justify-center rounded',
                    'text-text-tertiary hover:bg-surface-2 hover:text-danger',
                  )}
                >
                  <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                </button>
                <Button
                  variant="secondary"
                  onClick={() => setScheduleMode('menu')}
                  className="min-h-control gap-1.5 px-2.5 text-small"
                >
                  <CalendarClock size={14} strokeWidth={1.5} aria-hidden />
                  Schedule
                </Button>
              </div>
            </div>
          )
        }
      >
        <div className="flex flex-col gap-5">
          {/* Title: primary affordance, then everything else groups below. */}
          <div className="min-w-0">
            <label htmlFor="task-title" className="sr-only">
              Title
            </label>
            <textarea
              key={task.id}
              id="task-title"
              value={draft.title}
              onChange={(e) => {
                set('title', e.target.value);
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${el.scrollHeight}px`;
              }}
              onBlur={flush}
              rows={1}
              className={cn(
                'w-full resize-none overflow-hidden rounded border border-transparent bg-transparent',
                'text-heading font-medium leading-snug outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'hover:border-border focus:border-accent-border',
                'px-1.5 py-0.5 -mx-1.5',
                isDone && 'text-text-tertiary line-through',
              )}
            />
          </div>

          <DetailSection title="Plan">
            <Segmented
              compact
              label="Status"
              value={draft.status}
              options={STATUSES}
              onChange={(v) => {
                set('status', v, true);
                // Only on the transition into done -- re-picking done on an
                // already-finished task is not a completion.
                if (v === 'done' && draft.status !== 'done') {
                  askForNote(task.id, draft.title);
                }
              }}
            />

            <div>
              <label className="mb-1 block text-label font-medium uppercase tracking-wide text-text-secondary">
                Estimate
              </label>
              <div
                className={cn(
                  'flex items-stretch gap-px rounded-lg bg-surface-2 p-px',
                  'shadow-[inset_0_1px_0_oklch(1_0_0/0.04)]',
                )}
              >
                {QUICK_ESTIMATES.map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() =>
                      set(
                        'estimate_minutes',
                        draft.estimate_minutes === minutes ? null : minutes,
                        true,
                      )
                    }
                    aria-pressed={draft.estimate_minutes === minutes}
                    className={cn(
                      'pressable min-h-control flex-1 rounded-md px-1 text-label tabular',
                      'transition-[color,background-color,transform] duration-press',
                      'ease-[cubic-bezier(0.32,0.72,0,1)]',
                      draft.estimate_minutes === minutes
                        ? 'bg-accent font-medium text-text-on-accent'
                        : 'bg-transparent text-text-secondary hover:bg-surface-1 hover:text-text',
                    )}
                  >
                    {formatDuration(minutes)}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={draft.estimate_minutes ?? ''}
                  onChange={(e) =>
                    set('estimate_minutes', e.target.value ? Number(e.target.value) : null)
                  }
                  onBlur={flush}
                  aria-label="Estimate in minutes"
                  placeholder="min"
                  className={cn(
                    'tabular min-h-control w-12 shrink-0 rounded-md border-0 bg-surface-1',
                    'px-1.5 text-center text-label outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'focus:ring-1 focus:ring-[var(--accent-border)]',
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
              <Segmented
                compact
                label="Priority"
                value={draft.priority}
                options={PRIORITIES}
                onChange={(v) => set('priority', v, true)}
              />
              <Segmented
                compact
                label="Energy"
                value={draft.energy}
                options={ENERGIES}
                onChange={(v) => set('energy', v, true)}
              />
            </div>
          </DetailSection>

          <DetailSection title="Timing">
            <div className="grid grid-cols-2 gap-2">
              <DateField
                compact
                label="Due"
                timezone={timezone}
                value={draft.due_at}
                onChange={(v) => set('due_at', v, true)}
              />
              <DateField
                compact
                label="Start after"
                hint="Hidden from backlog until then."
                timezone={timezone}
                value={draft.defer_until}
                onChange={(v) => set('defer_until', v, true)}
              />
            </div>
            <RecurrenceSection taskId={task.id} compact />
          </DetailSection>

          <DetailSection title="Details">
            {/* Where this task sits in the tree. Stated even when it is
                top-level, because "no parent" is a fact about the task and not
                an absence of one. */}
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <span className="mb-1 block text-label font-medium uppercase tracking-wide text-text-secondary">
                  Parent
                </span>
                {parentTitle && onOpenTask && task.parent_task_id != null ? (
                  <button
                    type="button"
                    onClick={() => onOpenTask(task.parent_task_id!)}
                    title={parentTitle}
                    className="block w-full truncate rounded text-left text-small text-text hover:underline"
                  >
                    {parentTitle}
                  </button>
                ) : (
                  <p className="truncate text-small text-text" title={parentTitle ?? undefined}>
                    {parentLabel}
                  </p>
                )}
              </div>
              <Button
                variant="secondary"
                onClick={() => setMoving(true)}
                className="min-h-control shrink-0 gap-1.5 px-2.5 text-small"
              >
                <FolderTree size={14} strokeWidth={1.5} aria-hidden />
                Move
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <label
                  htmlFor="task-client"
                  className="mb-1 block text-label font-medium uppercase tracking-wide text-text-secondary"
                >
                  Client
                </label>
                {isRootTask ? (
                  <select
                    id="task-client"
                    value={draft.client_id}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setDraft((d) => ({ ...d, client_id: next, project_id: null }));
                      push({ client_id: next, project_id: null }, true);
                    }}
                    className={cn(
                      'min-h-control w-full rounded border border-border bg-surface-1 px-2',
                      'text-small outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border',
                    )}
                  >
                    {clients.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="min-h-control truncate rounded border border-border bg-surface-2 px-2 py-1.5 text-small text-text-secondary">
                    {clientName ?? '…'}
                    <span className="mt-0.5 block text-label font-normal normal-case text-text-tertiary">
                      Inherited from the parent task
                    </span>
                  </p>
                )}
              </div>

              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <label
                    htmlFor="task-project"
                    className="mb-1 block text-label font-medium uppercase tracking-wide text-text-secondary"
                  >
                    Project
                  </label>
                  <select
                    id="task-project"
                    value={draft.project_id ?? ''}
                    onChange={(e) =>
                      set('project_id', e.target.value ? Number(e.target.value) : null, true)
                    }
                    className={cn(
                      'min-h-control w-full rounded border border-border bg-surface-1 px-2',
                      'text-small outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border',
                    )}
                  >
                    <option value="">No project</option>
                    {projects.data?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              <label
                className={cn(
                  'pressable flex min-h-control shrink-0 items-center gap-1.5 rounded border px-2.5',
                  'text-label transition-colors duration-press',
                  draft.billable
                    ? 'border-accent-border bg-accent-quiet text-text'
                    : 'border-border bg-surface-1 text-text-secondary hover:border-border-strong',
                )}
              >
                <input
                  type="checkbox"
                  checked={draft.billable}
                  onChange={(e) => set('billable', e.target.checked, true)}
                  className="size-icon accent-[var(--accent)]"
                />
                Billable
              </label>
            </div>
            </div>

            <div>
              <label
                htmlFor="task-notes"
                className="mb-1 block text-label font-medium uppercase tracking-wide text-text-secondary"
              >
                Notes
              </label>
              <textarea
                id="task-notes"
                value={draft.notes ?? ''}
                onChange={(e) => set('notes', e.target.value || null)}
                onBlur={flush}
                rows={2}
                placeholder="Optional"
                className={cn(
                  'w-full resize-y rounded border border-border bg-surface-1 px-2 py-1.5',
                  'text-small outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary',
                  'focus:border-accent-border',
                )}
              />
            </div>
          </DetailSection>

          <DetailSection
            title="Subtasks"
            trailing={
              descendantCount > 0 ? (
                <span className="tabular text-label text-text-tertiary">
                  {doneDescendants}/{descendantCount} done
                </span>
              ) : undefined
            }
          >
            <SubtaskList parent={task} onOpenTask={onOpenTask} />
          </DetailSection>

          <DetailSection
            title="Time"
            trailing={
              <span className="tabular text-label text-text-tertiary">
                {formatClock(draft.rollup_logged_seconds)} logged
              </span>
            }
          >
            <div className="flex flex-wrap gap-1">
              {[15, 30, 60].map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => logTime.mutate({ minutes })}
                  disabled={logTime.isPending}
                  className={cn(
                    'pressable inline-flex min-h-control items-center gap-1 rounded-full px-2.5',
                    'bg-surface-2 text-label text-text-secondary',
                    'transition-[background-color,transform] duration-press',
                    'ease-[cubic-bezier(0.32,0.72,0,1)]',
                    'hover:bg-surface-3 disabled:opacity-50',
                  )}
                >
                  <Plus size={11} strokeWidth={1.5} aria-hidden />
                  {formatDuration(minutes)}
                </button>
              ))}
            </div>

            {entries.data && entries.data.length > 0 ? (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {entries.data.slice(0, 8).map((entry) => (
                  <TimeEntryRow
                    key={entry.id}
                    entry={entry}
                    saving={updateEntry.isPending}
                    onSave={(seconds) => updateEntry.mutate({ id: entry.id, seconds })}
                    onRemove={() => removeEntry(entry)}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-label text-text-tertiary">No time logged yet.</p>
            )}
          </DetailSection>
        </div>
      </Sheet>

      {moving && <MoveTaskSheet task={task} onClose={() => setMoving(false)} />}

      {scheduleMode === 'menu' && (
        <AssignToBlockSheet
          task={draft}
          timezone={timezone}
          submitting={assigningBlock || placement.pending != null}
          onClose={() => setScheduleMode(null)}
          onPickTime={() => setScheduleMode('time')}
          onPickBlock={(block) => void assignToBlock(block)}
        />
      )}

      {scheduleMode === 'time' && (
        <ScheduleSheet
          task={draft}
          timezone={timezone}
          onClose={() => setScheduleMode('menu')}
          onSubmit={(startsAt, endsAt) => {
            void placement.place(task.id, draft.title, new Date(startsAt), new Date(endsAt));
          }}
        />
      )}

      {placement.pending && (
        <ConflictChoiceSheet
          taskTitle={placement.pending.taskTitle}
          startsAt={placement.pending.startsAt}
          endsAt={placement.pending.endsAt}
          timezone={timezone}
          canSplit={placement.canSplit}
          onClose={placement.cancel}
          onChoose={(choice) =>
            choice.mode === 'split' ? placement.split() : placement.overlap()
          }
        />
      )}
    </>
  );
}

function DetailSection({
  title,
  trailing,
  children,
}: {
  title: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5 border-t border-border pt-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-label font-medium uppercase tracking-[0.08em] text-text-tertiary">
          {title}
        </h3>
        {trailing}
      </div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  );
}

function TimeEntryRow({
  entry,
  saving,
  onSave,
  onRemove,
}: {
  entry: TimeEntry;
  saving: boolean;
  onSave: (seconds: number) => void;
  onRemove: () => void;
}) {
  const closed = entry.duration_seconds != null;
  const [value, setValue] = useState(() =>
    entry.duration_seconds ? formatDurationClock(entry.duration_seconds) : '',
  );

  useEffect(() => {
    setValue(entry.duration_seconds ? formatDurationClock(entry.duration_seconds) : '');
  }, [entry.id, entry.duration_seconds]);

  const commit = () => {
    if (entry.duration_seconds == null) return;
    const seconds = parseDurationSeconds(value);
    if (seconds == null || seconds === entry.duration_seconds) {
      setValue(formatDurationClock(entry.duration_seconds));
      return;
    }
    onSave(seconds);
  };

  const date = new Date(entry.started_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });

  return (
    <li className="flex items-center justify-between gap-2 px-2 py-1.5 text-small text-text-secondary">
      <span className="flex min-w-0 items-center gap-1.5">
        <Clock size={12} strokeWidth={1.5} aria-hidden />
        {date}
        {entry.source === 'manual' && <span className="text-text-tertiary">· logged</span>}
      </span>
      <span className="flex shrink-0 items-center gap-0.5">
        {closed ? (
          <input
            type="text"
            inputMode="numeric"
            aria-label={`Time logged ${date}`}
            disabled={saving}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
                (e.target as HTMLInputElement).blur();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setValue(formatDurationClock(entry.duration_seconds ?? 0));
              }
            }}
            className={cn(
              'tabular w-20 rounded border border-transparent bg-transparent px-1 py-0.5 text-right',
              'text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'hover:border-border focus:border-accent-border',
              'disabled:opacity-50',
            )}
          />
        ) : (
          <span className="tabular px-1 text-text-tertiary">running</span>
        )}
        {closed && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove time logged ${date}`}
            className={cn(
              'pressable inline-flex size-7 items-center justify-center rounded',
              'text-text-tertiary transition-colors duration-press',
              'hover:bg-surface-2 hover:text-danger',
            )}
          >
            <Trash2 size={12} strokeWidth={1.5} aria-hidden />
          </button>
        )}
      </span>
    </li>
  );
}

function DateField({
  label,
  hint,
  value,
  onChange,
  timezone,
  compact = false,
}: {
  label: string;
  hint?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  timezone: string;
  compact?: boolean;
}) {
  return (
    <div>
      <label
        className={cn(
          'mb-1 block font-medium text-text-secondary',
          compact ? 'text-label uppercase tracking-wide' : 'text-small',
        )}
      >
        {label}
      </label>
      <input
        type="datetime-local"
        value={toLocalInput(value, timezone)}
        onChange={(e) => onChange(fromLocalInput(e.target.value, timezone))}
        className={cn(
          'tabular w-full rounded border border-border bg-surface-1 px-2 text-small outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border',
          compact ? 'min-h-control' : 'min-h-touch',
        )}
      />
      {hint && <p className="mt-0.5 text-label text-text-tertiary">{hint}</p>}
    </div>
  );
}

function SaveIndicator({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'idle') {
    return <span className="text-label text-text-tertiary">Changes save automatically</span>;
  }
  if (status === 'saving') return <span className="text-label text-text-tertiary">Saving…</span>;
  if (status === 'error') {
    return (
      <span role="alert" className="text-label text-danger">
        Could not save
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-label text-success">
      <Check size={12} strokeWidth={2} aria-hidden />
      Saved
    </span>
  );
}
